import { useRef } from "react";
import { useIntl } from "react-intl";
import { useScroll } from "src/ScrollContext";
import type { AccomPromoLoggingCategory } from "src/analytics/analyticsEventTypes";
import { border_radius } from "src/design-system/tokens/border";
import { spacing } from "src/design-system/tokens/spacing";
import ArrowForward from "src/design-system/icons/ArrowForward.svg?react";
import { useIsTripScreen } from "src/utils/hooks/useIsTripScreen";
import { useNavigateToHotelsPage } from "src/utils/hooks/useNavigateToHotelsPage";
import useSearch from "src/utils/hooks/useSearch";
import styled, { css } from "styled-components";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { useAnalyticsEventHasEnteredViewport } from "src/utils/hooks/useAnalyticsEventHasEnteredViewport";
import { tw } from "src/design-system/tw";
import { DottedLine, Timeline } from "../Timeline";
import { color, screenMinWidth } from "../../../theme";

import type { AccommodationPromoCell } from "./AccommodationPromoCell";
import messages from "./TroniconTimelineAccommodationPromo.messages";

type Props = {
  cell: AccommodationPromoCell;
  loggingFrom: AccomPromoLoggingCategory;
  showCardLayout?: boolean;
  isLineJoined?: boolean;
  className?: string;
};

export function TroniconTimelineAccommodationPromo({
  cell,
  loggingFrom,
  showCardLayout,
  isLineJoined,
  className,
}: Props) {
  const intl = useIntl();
  const copyPreposition = cell.useNearbyCopy ? "Near" : "In";
  const calloutMessage = messages[`findHotels${copyPreposition}`];
  const { navigateToHotels } = useNavigateToHotelsPage();
  const { setScrollTop } = useScroll();
  const { origin, destination } = useSearch();
  const isTripsScreen = useIsTripScreen();

  const accomCtaMessage = intl.formatMessage(messages.findHotelsCta);

  // We want to see when an accommodation promo gets into view but only fire the log once.
  const accommodationPromoRef = useRef<HTMLDivElement>(null);
  useAnalyticsEventHasEnteredViewport({
    ref: accommodationPromoRef,
    category: "Screen",
    action: "AccomPromoVisible",
    label: `ap0:${cell.provider}`,
  });

  function handleClick() {
    const gaLoggingCategory =
      loggingFrom === "Schedule" ? "ScheduleDetails" : loggingFrom;

    setScrollTop(0);
    if (isTripsScreen) {
      navigateToHotels({
        originCanonical: origin?.canonicalName,
        destinationCanonical: destination?.canonicalName,
      });
      sendAnalyticsInteractionEvent({
        category: "TripPlanner",
        action: "Click:HotelPromo",
      });
    } else {
      navigateToHotels();
      sendAnalyticsInteractionEvent({
        category: gaLoggingCategory,
        action: "Click:HotelPromo",
      });
    }
  }

  const extraLeftPadding = !showCardLayout && loggingFrom === "Schedule";

  return (
    <div className="flex flex-col">
      {isLineJoined && (
        <div
          className={tw("relative h-6", extraLeftPadding && "pl-27.5 md:pl-6")}
        >
          <div className="absolute top-0 -bottom-2 flex w-8 items-center justify-center">
            <DottedLine />
          </div>
        </div>
      )}
      <AccommodationWrapper
        $showCardLayout={showCardLayout}
        $extraLeftPadding={!showCardLayout && loggingFrom === "Schedule"}
        ref={accommodationPromoRef}
        onClick={handleClick}
        className={className}
      >
        <Timeline type="accommodation" transitMode="train" />
        <div className="flex flex-grow-1 flex-col justify-center leading-tight">
          <div className="flex flex-wrap items-center gap-x-2 text-base font-bold">
            <span>
              {intl.formatMessage(calloutMessage, { location: cell.placeName })}
            </span>
            <div className="flex items-center gap-2 leading-none text-text-brand">
              <span>{accomCtaMessage}</span>
              <ArrowForward
                className="h-4.5 w-4.5 text-text-brand"
                title="open-hotels-arrow"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </AccommodationWrapper>
    </div>
  );
}

const AccommodationWrapper = styled.div<{
  $showCardLayout?: boolean;
  $extraLeftPadding?: boolean;
}>`
  display: flex;
  padding: ${spacing.lg} ${spacing.xl};
  gap: ${spacing.xl};
  cursor: pointer;

  ${(props) => {
    if (props.$showCardLayout) {
      return `
          border: 1px solid ${color.grey2};
          border-radius: ${border_radius.rounded_md};
          `;
    }
  }}

  ${({ $extraLeftPadding }) =>
    $extraLeftPadding &&
    css`
      padding-left: 110px;
      ${screenMinWidth.sm} {
        padding-left: ${spacing.xxl};
      }
    `}

  &:active {
    background-color: ${color.grey1};
  }
`;
